<template>
  <div
    class="freight-route-taxes"
    :class="{ 'freight-route-taxes--compact': compact }"
  >
    <a-radio-group
      :value="tab"
      class="freight-route-taxes__segmented"
      @change="(e) => (tab = e.target.value)"
    >
      <a-radio-button v-for="aba in TABS" :key="aba.key" :value="aba.key">
        {{ aba.label }}
        <span
          v-if="tabHasError(aba.key)"
          class="freight-route-taxes__tab-error"
          aria-label="Aba com erro"
        />
      </a-radio-button>
    </a-radio-group>

    <div v-if="tab === 'tda'" class="freight-route-taxes__tda">
      <div
        class="freight-route-taxes__grid-tipos"
        :class="{ 'freight-route-taxes__grid-tipos--status': hasStatus }"
      >
        <div
          v-for="grupo in TAXA_GROUPS"
          :key="`tipo-${grupo.sufixo}`"
          class="freight-route-taxes__field"
          :class="fieldClass(taxaError(grupo.tipo))"
        >
          <span class="freight-route-taxes__label">
            Tipo taxa {{ grupo.sufixo }}
          </span>
          <a-select
            :value="selectValue(value[grupo.tipo])"
            allow-clear
            :placeholder="`Selecione o tipo taxa ${grupo.sufixo}`"
            @change="(v) => patch(grupo.tipo, selectNumber(v))"
          >
            <a-select-option
              v-for="t in list(grupo.opcoes)"
              :key="`${grupo.sufixo}-${t.value}`"
              :value="t.value"
            >
              {{ t.label }}
            </a-select-option>
          </a-select>
          <p v-if="taxaError(grupo.tipo)" class="freight-route-taxes__error">
            {{ taxaError(grupo.tipo) }}
          </p>
        </div>

        <div v-if="hasStatus" class="freight-route-taxes__status-field">
          <div class="freight-route-taxes__status">
            <span class="freight-route-taxes__status-label">Status</span>
            <a-switch
              :checked="status === STATUS_ATIVO"
              :disabled="statusDisabled"
              aria-label="Status"
              @change="(checked) => $emit('status-change', checked ? STATUS_ATIVO : STATUS_INATIVO)"
            />
          </div>
          <p v-if="statusError" class="freight-route-taxes__error">
            {{ statusError }}
          </p>
        </div>
      </div>

      <div class="freight-route-taxes__divider" />

      <div class="freight-route-taxes__grid-valores">
        <template v-for="grupo in TAXA_GROUPS">
          <div
            :key="`taxa-${grupo.sufixo}`"
            class="freight-route-taxes__field"
            :class="fieldClass(taxaError(grupo.taxa))"
          >
            <span class="freight-route-taxes__label">
              Taxa {{ grupo.sufixo }}
            </span>
            <a-input
              :value="taxDecimalInputValue(grupo.taxa, value[grupo.taxa])"
              placeholder="Informe o valor"
              @focus="(e) => onTaxDecimalFocus(grupo.taxa, value[grupo.taxa], e)"
              @input="(e) => onTaxDecimalInput(grupo.taxa, e, (num) => patch(grupo.taxa, num))"
              @blur="(e) => onTaxDecimalBlur(grupo.taxa, e, (num) => patch(grupo.taxa, num))"
            />
            <p v-if="taxaError(grupo.taxa)" class="freight-route-taxes__error">
              {{ taxaError(grupo.taxa) }}
            </p>
          </div>

          <div
            :key="`fixo-${grupo.sufixo}`"
            class="freight-route-taxes__field"
            :class="fieldClass(taxaError(grupo.fixo))"
          >
            <span class="freight-route-taxes__label">Valor fixo</span>
            <a-input
              :value="taxDecimalInputValue(grupo.fixo, value[grupo.fixo])"
              placeholder="Informe o valor"
              @focus="(e) => onTaxDecimalFocus(grupo.fixo, value[grupo.fixo], e)"
              @input="(e) => onTaxDecimalInput(grupo.fixo, e, (num) => patch(grupo.fixo, num))"
              @blur="(e) => onTaxDecimalBlur(grupo.fixo, e, (num) => patch(grupo.fixo, num))"
            />
            <p v-if="taxaError(grupo.fixo)" class="freight-route-taxes__error">
              {{ taxaError(grupo.fixo) }}
            </p>
          </div>
        </template>
      </div>
    </div>

    <div v-else-if="tab === 'pedagio'" class="freight-route-taxes__grid-pedagio">
      <div
        v-for="campo in PEDAGIO_FIELDS"
        :key="campo.campo"
        class="freight-route-taxes__field"
        :class="fieldClass(taxaError(campo.campo))"
      >
        <span class="freight-route-taxes__label" :title="campo.label">{{ campo.label }}</span>
        <a-select
          v-if="campo.opcoes"
          :value="selectValue(value[campo.campo])"
          allow-clear
          :placeholder="campo.placeholder"
          @change="(v) => patch(campo.campo, selectNumber(v))"
        >
          <a-select-option
            v-for="t in list(campo.opcoes)"
            :key="t.value"
            :value="t.value"
          >
            {{ t.label }}
          </a-select-option>
        </a-select>
        <a-input
          v-else
          :value="taxDecimalInputValue(campo.campo, value[campo.campo])"
          placeholder="Informe o valor"
          @focus="(e) => onTaxDecimalFocus(campo.campo, value[campo.campo], e)"
          @input="(e) => onTaxDecimalInput(campo.campo, e, (num) => patch(campo.campo, num))"
          @blur="(e) => onTaxDecimalBlur(campo.campo, e, (num) => patch(campo.campo, num))"
        />
        <p v-if="taxaError(campo.campo)" class="freight-route-taxes__error">
          {{ taxaError(campo.campo) }}
        </p>
      </div>
    </div>

    <div v-else-if="tab === 'outros'" class="freight-route-taxes__grid-outros">
      <div
        v-for="campo in OUTROS_FIELDS"
        :key="campo.campo"
        class="freight-route-taxes__field"
        :class="fieldClass(taxaError(campo.campo))"
      >
        <span class="freight-route-taxes__label" :title="campo.label">{{ campo.label }}</span>
        <a-input
          :value="
            campo.inteiro
              ? taxDisplay(value[campo.campo])
              : taxDecimalInputValue(campo.campo, value[campo.campo])
          "
          :placeholder="campo.placeholder || 'Informe o valor'"
          @focus="
            (e) =>
              !campo.inteiro &&
              onTaxDecimalFocus(campo.campo, value[campo.campo], e)
          "
          @input="
            (e) =>
              campo.inteiro
                ? patchInteger(campo.campo, e)
                : onTaxDecimalInput(campo.campo, e, (num) =>
                    patch(campo.campo, num),
                  )
          "
          @blur="
            (e) =>
              !campo.inteiro &&
              onTaxDecimalBlur(campo.campo, e, (num) => patch(campo.campo, num))
          "
        />
        <p v-if="taxaError(campo.campo)" class="freight-route-taxes__error">
          {{ taxaError(campo.campo) }}
        </p>
      </div>
    </div>

    <div v-else class="freight-route-taxes__custos">
      <div
        v-for="(custo, index) in custos"
        :key="index"
        class="freight-route-taxes__custo-row"
      >
        <div
          v-for="campo in CUSTO_FIELDS"
          :key="campo.campo"
          class="freight-route-taxes__field"
          :class="fieldClass(custoError(index, campo.campo))"
        >
          <span class="freight-route-taxes__label" :title="campo.label">{{ campo.label }}</span>
          <a-select
            v-if="campo.opcoes"
            :value="selectValue(custo[campo.campo])"
            allow-clear
            :show-search="campo.busca"
            option-filter-prop="children"
            :placeholder="campo.placeholder"
            @change="(v) => patchCusto(index, { [campo.campo]: selectNumber(v) })"
          >
            <a-select-option
              v-for="t in optionsFor(campo)"
              :key="t.value"
              :value="t.value"
            >
              {{ t.label }}
            </a-select-option>
          </a-select>
          <a-input
            v-else
            :value="taxDecimalInputValue(custoDecimalKey(index, campo.campo), custo[campo.campo])"
            :placeholder="campo.placeholder"
            @focus="(e) => onTaxDecimalFocus(custoDecimalKey(index, campo.campo), custo[campo.campo], e)"
            @input="(e) => onTaxDecimalInput(custoDecimalKey(index, campo.campo), e, (num) => patchCustoDecimal(index, campo.campo, num))"
            @blur="(e) => onTaxDecimalBlur(custoDecimalKey(index, campo.campo), e, (num) => patchCustoDecimal(index, campo.campo, num))"
          />
          <p
            v-if="custoError(index, campo.campo)"
            class="freight-route-taxes__error"
          >
            {{ custoError(index, campo.campo) }}
          </p>
        </div>

        <div class="freight-route-taxes__custo-actions">
          <a-tooltip
            v-if="index === 0"
            :title="canAddCusto ? '' : CUSTO_LIMIT_MESSAGE"
            placement="topRight"
          >
            <a-button
              type="link"
              class="freight-route-taxes__custo-action"
              :disabled="!canAddCusto"
              @click="addCusto"
            >
              <a-icon type="plus" /> Novo custo
            </a-button>
          </a-tooltip>
          <a-button
            v-else
            type="link"
            class="freight-route-taxes__custo-action freight-route-taxes__trash"
            :aria-label="`Remover custo ${index + 1}`"
            @click="removeCusto(index)"
          >
            <a-icon type="delete" />
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { CUSTO_EXTRA_MAX } from "../../constants/freightTableOptions";
import { FREIGHT_TABLE_STATUS } from "../../constants/freightTableStatus";
import {
  coerceOutrosDecimal,
  formatOutrosDecimal,
} from "../../utils/freightTableOutrosDecimal";
import {
  CUSTO_EXTRA_LIMIT,
  createEmptyCustoExtra,
} from "../../utils/validityTaxes";

const TABS = [
  { key: "tda", label: "TDA/TDE" },
  { key: "pedagio", label: "Pedágio" },
  { key: "outros", label: "Outros" },
  { key: "custo", label: "Custo" },
];

const TAXA_GROUPS = [
  {
    sufixo: "TDE",
    tipo: "tp_taxa_tde",
    taxa: "taxa_tde",
    fixo: "vlr_fixo_tde",
    opcoes: "tiposTaxaTde",
  },
  {
    sufixo: "TDA",
    tipo: "tp_taxa_tda",
    taxa: "taxa_tda",
    fixo: "vlr_fixo_tda",
    opcoes: "tiposTaxaTda",
  },
];

const PEDAGIO_FIELDS = [
  {
    campo: "tp_taxa_pedagio",
    label: "Tipo taxa pedágio",
    placeholder: "Selecione o tipo taxa pedágio",
    opcoes: "tiposTaxaPedagio",
  },
  { campo: "taxa_pedagio", label: "Pedágio por fração de" },
  { campo: "vlr_pedagio", label: "Valor" },
];

// Ordem da tela (outros.svg). Os placeholders do Figma são resíduo de
// copy/paste ("Selecione o tipo taxa TDE" em campo de valor) — ignorados.
// A textarea de descrição do Figma é obs_valores e ficou fora: não persiste
// no back (ADR-0011 §3).
const OUTROS_FIELDS = [
  { campo: "taxa_gris", label: "Taxa gris" },
  { campo: "vlr_minimo_gris", label: "Mínimo gris" },
  { campo: "ad_valorem", label: "Ad Valorem" },
  { campo: "vlr_minimo_advalorem", label: "Mínimo advalorem" },
  { campo: "vlr_min_nota_cte", label: "Valor mínimo nota/CTe" },
  { campo: "vlr_tas", label: "Valor TAS" },
  { campo: "vlr_sec_cat", label: "Valor SEC/CAT" },
  { campo: "vlr_despacho", label: "Valor despacho" },
  { campo: "vlr_agendamento", label: "Valor agendamento" },
  { campo: "vlr_outros", label: "Valor outros" },
  { campo: "taxa_devolucao", label: "Taxa devolução" },
  { campo: "taxa_reentrega", label: "Taxa reentrega" },
  { campo: "taxa_coleta", label: "Taxa de coleta" },
  // Coluna vlr_qtde_docs_fiscais — o nome não descreve o campo (ver glossário).
  { campo: "vlr_qtde_docs_fiscais", label: "Valor mínimo por rota" },
  {
    campo: "qtde_docs_fiscais",
    label: "Quantidade de notas",
    placeholder: "Informe a quantidade",
    inteiro: true,
  },
];

const CUSTO_FIELDS = [
  {
    campo: "tp_trecho",
    label: "Tipo trecho",
    placeholder: "Selecione o tipo",
    opcoes: "tpTrecho",
  },
  {
    campo: "tp_trecho_custo",
    label: "Tipo trecho custo",
    placeholder: "Selecione o tipo",
    opcoes: "tpTrechoCusto",
  },
  { campo: "vlr_taxa", label: "Taxa", placeholder: "Informe o valor" },
  {
    campo: "taxa_comissao",
    label: "Taxa (%)",
    placeholder: "Informe o valor em % (máx. 99,999)",
  },
  { campo: "vlr_minimo", label: "Valor mínimo", placeholder: "Informe o valor" },
  {
    campo: "custo_id",
    label: "Custo",
    placeholder: "Selecione o custo",
    opcoes: "custos",
    busca: true,
  },
];

const TAB_FIELDS = {
  tda: ["tp_taxa_tde", "tp_taxa_tda", "taxa_tde", "taxa_tda", "vlr_fixo_tde", "vlr_fixo_tda"],
  pedagio: PEDAGIO_FIELDS.map((campo) => campo.campo),
  outros: OUTROS_FIELDS.map((campo) => campo.campo),
};

const CUSTO_LIMIT_MESSAGE = `É possível informar até ${CUSTO_EXTRA_LIMIT} custos por vigência.`;

/**
 * As quatro abas de taxas de uma vigência. Recebe o objeto da vigência (ou
 * as taxas do bloco de nova vigência) e emite o partial alterado.
 */
export default {
  name: "FreightTableRouteTaxes",
  props: {
    value: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({ taxas: {}, custos: {} }),
    },
    compact: {
      type: Boolean,
      default: false,
    },
    excludedTrechos: {
      type: Array,
      default: () => [],
    },
    status: {
      type: Number,
      default: null,
    },
    statusDisabled: {
      type: Boolean,
      default: false,
    },
    statusError: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      TABS,
      TAXA_GROUPS,
      PEDAGIO_FIELDS,
      OUTROS_FIELDS,
      CUSTO_FIELDS,
      CUSTO_LIMIT_MESSAGE,
      STATUS_ATIVO: FREIGHT_TABLE_STATUS.ATIVA,
      STATUS_INATIVO: FREIGHT_TABLE_STATUS.INATIVA,
      tab: "tda",
      /** Texto livre enquanto um valor decimal está focado (evita pular cursor para ",000"). */
      taxDecimalDraft: {},
    };
  },
  watch: {
    tab() {
      this.taxDecimalDraft = {};
    },
  },
  computed: {
    ...mapState("freightTables", ["options"]),
    /** Sempre ao menos uma linha: o Figma mostra a grade preenchível de saída. */
    custos() {
      const lista = this.value.custo_extra;
      return lista && lista.length ? lista : [createEmptyCustoExtra()];
    },
    hasStatus() {
      return this.status !== null && this.status !== undefined;
    },
    canAddCusto() {
      return this.custos.length < CUSTO_EXTRA_LIMIT;
    },
  },
  methods: {
    /** Listas de domínio vêm da API e são null enquanto carregam. */
    list(name) {
      return (this.options && this.options[name]) || [];
    },
    optionsFor(campo) {
      const options = this.list(campo.opcoes);
      if (campo.campo !== "tp_trecho" || !this.excludedTrechos.length) {
        return options;
      }
      const excluded = this.excludedTrechos.map(Number);
      return options.filter(
        (option) => excluded.indexOf(Number(option.value)) === -1,
      );
    },
    taxaError(campo) {
      const taxas = (this.errors && this.errors.taxas) || {};
      return taxas[campo] || "";
    },
    custoError(index, campo) {
      const custos = (this.errors && this.errors.custos) || {};
      const linha = custos[index];
      return linha ? linha[campo] || "" : "";
    },
    tabHasError(key) {
      if (key === "custo") {
        const custos = (this.errors && this.errors.custos) || {};
        return Object.keys(custos).some((index) =>
          Object.keys(custos[index] || {}).some((campo) => custos[index][campo]),
        );
      }
      if (key === "tda" && this.statusError) return true;
      return TAB_FIELDS[key].some((campo) => this.taxaError(campo));
    },
    fieldClass(message) {
      return { "freight-route-taxes__field--error": !!message };
    },
    selectValue(value) {
      return value === null || value === undefined ? undefined : value;
    },
    selectNumber(value) {
      return value === null || value === undefined || value === ""
        ? null
        : Number(value);
    },
    taxDisplay(value) {
      return value === 0 || value === null || value === undefined ? "" : value;
    },
    taxDecimalDisplay(value) {
      if (value === 0 || value === null || value === undefined || value === "") {
        return "";
      }

      return formatOutrosDecimal(value);
    },
    custoDecimalKey(index, campo) {
      return `custo:${index}:${campo}`;
    },
    taxDecimalInputValue(draftKey, value) {
      if (Object.prototype.hasOwnProperty.call(this.taxDecimalDraft, draftKey)) {
        return this.taxDecimalDraft[draftKey];
      }

      return this.taxDecimalDisplay(value);
    },
    onTaxDecimalFocus(draftKey, value, e) {
      this.$set(this.taxDecimalDraft, draftKey, this.taxDecimalDisplay(value));
      this.$nextTick(() => {
        if (e && e.target && typeof e.target.select === "function") {
          e.target.select();
        }
      });
    },
    onTaxDecimalInput(draftKey, e, onCommit) {
      const raw = e && e.target ? e.target.value : e;
      const text = raw === null || raw === undefined ? "" : String(raw);
      this.$set(this.taxDecimalDraft, draftKey, text);
      const num = text.trim() === "" ? 0 : coerceOutrosDecimal(text, 0);
      onCommit(num);
    },
    onTaxDecimalBlur(draftKey, e, onCommit) {
      const raw = e && e.target ? e.target.value : "";
      const num =
        String(raw || "").trim() === "" ? 0 : coerceOutrosDecimal(raw, 0);
      onCommit(num);
      this.$delete(this.taxDecimalDraft, draftKey);
    },
    patch(campo, valor) {
      this.$emit("patch", { [campo]: valor });
    },
    patchInteger(campo, e) {
      const raw = String((e && e.target ? e.target.value : e) || "").replace(
        /\D/g,
        "",
      );
      this.patch(campo, raw === "" ? null : Number(raw));
    },
    patchCusto(index, partial) {
      this.patch(
        "custo_extra",
        this.custos.map((item, i) =>
          i === index ? { ...item, ...partial } : item,
        ),
      );
    },
    patchCustoDecimal(index, campo, num) {
      const max = CUSTO_EXTRA_MAX[campo];
      const safe = max !== undefined && num > max ? max : num;
      this.patchCusto(index, { [campo]: safe });
    },
    addCusto() {
      if (!this.canAddCusto) return;
      this.patch("custo_extra", [...this.custos, createEmptyCustoExtra()]);
    },
    removeCusto(index) {
      this.patch(
        "custo_extra",
        this.custos.filter((_, i) => i !== index),
      );
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-route-taxes {
  font-family: @font-primary;

  &__label {
    display: block;
    padding-bottom: 8px;
    overflow: hidden;
    color: rgba(0, 0, 0, 0.88);
    font-size: 14px;
    line-height: 22px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__field {
    min-width: 0;
  }

  &__field--error {
    /deep/ .ant-input,
    /deep/ .ant-select-selection {
      border-color: @color-danger;
    }
  }

  &__error {
    margin: 4px 0 0;
    color: @color-danger;
    font-size: 12px;
    line-height: 18px;
    white-space: pre-line;
  }

  /deep/ .ant-select {
    display: block;
    width: 100%;
  }

  &__tab-error {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 4px;
    border-radius: 50%;
    background: @color-danger;
    vertical-align: middle;
  }

  &__grid-tipos {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 12px;
    padding-top: 16px;
  }

  &__grid-tipos--status {
    grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  }

  &__status-field {
    padding-top: 30px;
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 32px;
  }

  &__status-label {
    color: rgba(0, 0, 0, 0.88);
    font-size: 14px;
    font-weight: 500;
  }

  &__status /deep/ .ant-switch-checked {
    background: @color-primary;
  }

  &__divider {
    height: 1px;
    margin: 16px 0;
    background: rgba(0, 0, 0, 0.06);
  }

  &__grid-valores {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px 12px;
  }

  &__grid-pedagio {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px 12px;
    padding-top: 16px;
  }

  &__grid-outros {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 16px 12px;
    padding-top: 16px;
  }

  &__custos {
    padding-top: 16px;
  }

  &__custo-row {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr)) auto;
    align-items: start;
    gap: 12px;

    & + & {
      margin-top: 12px;
    }
  }

  &__custo-actions {
    padding-top: 30px;
  }

  &__custo-action {
    padding: 0;
    height: 32px;
    white-space: nowrap;
  }

  &__trash {
    color: @color-danger-action;
  }

  &__segmented {
    display: inline-flex;
    padding: 2px;
    background: @color-bg-subtle;
    border-radius: 6px;

    /deep/ .ant-radio-button-wrapper {
      height: 28px;
      line-height: 28px;
      padding: 0 12px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      box-shadow: none;
      color: rgba(0, 0, 0, 0.65);

      &::before {
        display: none;
      }

      &:focus-within {
        box-shadow: none;
      }
    }

    /deep/ .ant-radio-button-wrapper-checked {
      background: #ffffff;
      color: rgba(0, 0, 0, 0.88);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03),
        0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px rgba(0, 0, 0, 0.02);
    }
  }

  &--compact {
    .freight-route-taxes__grid-outros {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .freight-route-taxes__custo-row {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      padding: 12px;
      border: 1px solid rgba(0, 0, 0, 0.06);
      border-radius: 8px;
    }

    .freight-route-taxes__custo-actions {
      grid-column: ~"1 / -1";
      padding-top: 0;
      text-align: right;
    }
  }

  /deep/ .ant-input,
  /deep/ .ant-select-selection {
    border-radius: 6px;
  }
}
</style>

<style lang="less">
@import "~@/modules/shared/styles/form-controls.less";

.freight-route-taxes {
  .module-form-controls();
}
</style>
